import * as React from 'react';
import { useBranding } from '@/contexts/branding-context';
import { cn } from '@/lib/utils';

export type BrandMarkProps = {
  className?: string;
  imgClassName?: string;
  /** Center logo for stacked layouts (e.g. login card). */
  centered?: boolean;
};

export function BrandMark({ className, imgClassName, centered = false }: BrandMarkProps) {
  const { branding } = useBranding();
  const [dark, setDark] = React.useState(() => document.documentElement.classList.contains('dark'));

  React.useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setDark(root.classList.contains('dark'));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const logoUrl = dark && branding.logoUrlDark ? branding.logoUrlDark : branding.logoUrl;
  const displayName = branding.brandName || '247 Alert';

  if (logoUrl) {
    return (
      <div className={cn('flex flex-col gap-0.5', centered && 'items-center', className)}>
        <img
          src={logoUrl}
          alt={displayName}
          className={cn(
            'h-[3.375rem] max-w-[15rem] object-contain',
            centered ? 'mx-auto object-center' : 'object-left',
            imgClassName,
          )}
        />
        <span className="sr-only">{displayName}</span>
      </div>
    );
  }

  return (
    <div className={cn(centered && 'text-center', className)}>
      <span className="brand-accent-text text-sm font-semibold tracking-[0.2em]">{displayName.toUpperCase()}</span>
      <span className="block text-lg font-bold text-foreground">{displayName}</span>
    </div>
  );
}
